import { useTheme } from '@mui/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getDurationString, roundHours } from '../../store/utils/worklogUtils'
import { convertToDays } from '../DateUtils/convertToDays'
import BasePercentageBar, { BarSegment, LegendItem } from './BasePercentageBar'

const getHoursTooltip = (hours: number, isDisplayDaysMode: boolean): string | undefined => {
  if (isDisplayDaysMode) return undefined
  if (Number.isInteger(hours)) return undefined
  return getDurationString(hours)
}

type VacationPercentageBarProps = {
  paidVacationHoursTotal: number
  usedPaidVacation: number
  allUnpaidVacationSum?: number
  isDisplayDaysMode?: boolean
}

const VacationPercentageBar = ({
  paidVacationHoursTotal,
  usedPaidVacation,
  allUnpaidVacationSum = 0,
  isDisplayDaysMode = false,
}: VacationPercentageBarProps) => {
  const theme = useTheme()
  const { t } = useTranslation()

  const unit = isDisplayDaysMode ? 'd' : 'h'

  const { segments, legends, totalAvailable, displayOnlyUnpaidSummary, titleLabel } = useMemo(() => {
    const totalAvailable = isDisplayDaysMode ? convertToDays(paidVacationHoursTotal) : paidVacationHoursTotal
    const displayOnlyUnpaidSummary = totalAvailable === 0
    const rawUsed = roundHours(usedPaidVacation)
    const effectiveUsed = rawUsed > 0 ? rawUsed : 0
    const usedBar = isDisplayDaysMode ? convertToDays(effectiveUsed) : effectiveUsed

    const usedColor = theme.palette.info.dark
    const unpaidColor = theme.palette.info.light
    const leftoverColor = theme.palette.secondary.light

    let usedPercentage = 0
    let leftoverPercentage = 0

    if (totalAvailable > 0) {
      usedPercentage = Math.min((usedBar / totalAvailable) * 100, 100)
      leftoverPercentage = Math.max(0, 100 - usedPercentage)
    } else if (usedBar > 0 || allUnpaidVacationSum > 0) {
      usedPercentage = 100
    }

    const leftoverBar = parseFloat((totalAvailable - usedBar).toFixed(2))
    const leftoverClamped = leftoverBar > 0 ? leftoverBar : 0

    const segments: BarSegment[] = []

    if (displayOnlyUnpaidSummary) {
      if (allUnpaidVacationSum > 0) {
        segments.push({
          percentage: 100,
          color: unpaidColor,
          value: isDisplayDaysMode ? convertToDays(allUnpaidVacationSum) : allUnpaidVacationSum,
          label: t('components.percentageBar.used'),
        })
      }
    } else {
      if (usedPercentage > 0) {
        segments.push({
          percentage: usedPercentage,
          color: usedColor,
          value: usedBar,
          label: t('components.percentageBar.used'),
        })
      }
      if (leftoverPercentage > 0) {
        segments.push({
          percentage: leftoverPercentage,
          color: leftoverColor,
          value: leftoverClamped,
          label: t('components.percentageBar.left'),
        })
      }
    }

    const legends: LegendItem[] = []

    if (displayOnlyUnpaidSummary) {
      const unpaidValue = isDisplayDaysMode ? convertToDays(allUnpaidVacationSum) : allUnpaidVacationSum
      legends.push({
        color: unpaidColor,
        value: `${unpaidValue}${unit}`,
        label: t('components.percentageBar.used'),
        tooltipValue: getHoursTooltip(allUnpaidVacationSum, isDisplayDaysMode),
      })
    } else {
      legends.push({
        color: usedColor,
        value: `${usedBar}${unit}`,
        label: t('components.percentageBar.used'),
        tooltipValue: getHoursTooltip(effectiveUsed, isDisplayDaysMode),
      })

      const leftoverIndicatorColor = leftoverBar < 0 ? theme.palette.error.main : leftoverColor
      const leftoverDisplayValue = leftoverBar < 0 ? -Math.abs(leftoverBar) : leftoverClamped
      const leftoverValue =
        leftoverBar < 0
          ? `${-Math.abs(isDisplayDaysMode ? convertToDays(leftoverBar) : leftoverBar)}${unit}`
          : `${leftoverClamped}${unit}`

      legends.push({
        color: leftoverIndicatorColor,
        value: leftoverValue,
        label: t('components.percentageBar.left'),
        tooltipValue: getHoursTooltip(Math.abs(leftoverDisplayValue), isDisplayDaysMode),
      })
    }

    const titleLabel = displayOnlyUnpaidSummary
      ? t('page.yearlySummary.unpaidVacationSummary')
      : t('page.yearlySummary.paidVacationSummary')

    return { segments, legends, totalAvailable, displayOnlyUnpaidSummary, titleLabel }
  }, [paidVacationHoursTotal, usedPaidVacation, allUnpaidVacationSum, isDisplayDaysMode, theme.palette, t, unit])

  return (
    <BasePercentageBar
      segments={segments}
      totalLabel={titleLabel}
      totalValue={totalAvailable}
      showTotal={!displayOnlyUnpaidSummary}
      legends={legends}
      unitLabel={isDisplayDaysMode ? t('components.percentageBar.year.days') : t('components.percentageBar.year.hours')}
    />
  )
}

export default VacationPercentageBar
